// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldClickable } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { searchInputStyles } from "@west/Overrides/Widgets/Header/SearchInput";
import Search from "@west/XDL/Icons/Search";
import { color, styledScrollbar, xScreen } from "@west/XDL/Utilities";
import { css } from "styled-components";

const maxHeight = 472;

searchInputStyles.input = {
    sizeVariant: "default",
    border: "rectangle",
    iconProps: {
        src: Search,
        color: "common.ui8",
    },
    cssOverrides: {
        formField: css`
            &:hover {
                & input,
                & ${FormFieldClickable} {
                    border-color: ${color("common.ui8")};
                }
            }

            ${FormFieldClickable} {
                ${xScreen("gt", "lg")} {
                    display: flex;
                    justify-content: center;
                    width: 42px;
                    height: 42px;
                    &:focus {
                        outline: none;
                    }
                    ${IconWrapper} {
                        padding: 0;
                        width: auto;
                        height: auto;
                        position: static;
                    }
                }
                svg {
                    height: auto;
                    width: 15px;
                }
            }
        `,
        inputSelect: css`
            padding-right: 45px;
            &:focus {
                padding-right: 45px;
                border-color: ${color("common.ui9")};
            }

            ${xScreen("gt", "lg")} {
                height: 42px;
            }
        `,
    },
};

searchInputStyles.autocompleteWrapper = {
    css: css`
        display: flex;
        flex-direction: row;
        max-height: ${maxHeight}px;
        max-width: 648px;

        & > div:first-child {
            width: 100%;
        }

        & > div:only-child {
            min-width: 648px;
        }

        ${xScreen("lte", "md")} {
            max-width: 100%;
            width: 100%;
            row-gap: 24px;
            & > div:only-child {
                min-width: 100%;
            }
        }

        ${xScreen("lt", "sm")} {
            flex-direction: column;
            ${({ headerHeight }: any) => css`
                ${headerHeight &&
                css`
                    height: calc(100vh - ${headerHeight});
                    max-height: calc(100vh - ${headerHeight});
                `};
            `}

            & > div {
                flex: 1;
            }
        }

        ${xScreen("gt", "sm")} {
            .has-product-column {
                &::before {
                    content: "";
                    background: ${color("common.ui3")};
                    width: 1px;
                    height: 100%;
                    position: absolute;
                    left: 0;
                    top: 0;
                }
            }
        }
    `,
};

searchInputStyles.autocompleteColumnWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        padding: 13px;
        position: relative;
        gap: 10px;
        max-height: ${maxHeight}px;
        width: 274px;
        height: 100%;
        ${styledScrollbar(4)}

        ${xScreen("lte", "md")} {
            padding: 13px 17px 13px 13px;
            width: 334px;
        }

        ${xScreen("lt", "sm")} {
            padding: 0 16px 24px;
            max-height: unset;
            width: 100%;
        }
    `,
};

searchInputStyles.autocompleteColumnProductWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        padding: 13px 13px 17px;
        max-height: ${maxHeight}px;
        ${styledScrollbar(4)}
        width: 100%;

        ${xScreen("lte", "md")} {
            overflow: auto;
            padding: 15px 8px 15px 16px;
            margin-right: 5px;
        }

        ${xScreen("lt", "sm")} {
            padding: 16px 16px 0 16px;
            max-height: unset;
            border-top: 1px solid ${color("common.border")};
        }
    `,
};

searchInputStyles.tippyPopup = {
    container: {
        css: css`
            max-width: 100%;

            &.tippy-box {
                border: 1px solid ${color("common.ui3")};
                background-color: ${color("common.white")};
                border-radius: 0;
                max-width: 648px !important;
                ${xScreen("lte", "md")} {
                    max-width: 100% !important;
                }

                & > .tippy-content {
                    padding: 0;

                    ${xScreen("lt", "sm")} {
                        max-height: 80vh;
                        overflow: auto;
                        ${styledScrollbar(4)}
                    }
                }
            }
        `,
    },
};

searchInputStyles.inputWrapper = {
    css: css`
        line-height: 0; // to fix extra spacing on firefox
    `,
};

searchInputStyles.searchPopUpWrapper = {
    css: css`
        ${({ width }: any) => `width: ${width}px;`}
        ${xScreen("lte", "md")} {
            width: 100%;
        }
    `,
};
